import { Stack, Typography, useTheme } from "@mui/material";
import type { Control } from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { BuildingSavingsAdditionalFields } from "web_myplann/Portfolio/Forms/BuildingSavingsForm/BuildingSavingsAdditionalFields";
import { BuildingSavingsBasicFields } from "web_myplann/Portfolio/Forms/BuildingSavingsForm/BuildingSavingsBasicFields";
import { BuildingSavingsContractFields } from "web_myplann/Portfolio/Forms/BuildingSavingsForm/BuildingSavingsContractFields";
import type {
	BuildingSavingsCreateFormInput,
	BuildingSavingsCreateFormOutput,
} from "web_myplann/Portfolio/Forms/BuildingSavingsForm/buildingSavingsFormSchema";
import {
	CONTRACT_REQUIRED_MINIMUM_FIELDS,
	getContractRequiredMinimumMessage,
} from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import { RequiredMinimumMessage } from "web_myplann/Portfolio/PortfolioShared/RequiredMinimumMessage";
import { DisclosureSection } from "web_myplann/Shared/Components/DisclosureSection";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type BuildingSavingsCreateFormFieldsProps = {
	control: Control<
		BuildingSavingsCreateFormInput,
		unknown,
		BuildingSavingsCreateFormOutput
	>;
};

export const BuildingSavingsCreateFormFields = ({
	control,
}: BuildingSavingsCreateFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();
	const today = new Date();

	return (
		<>
			<BuildingSavingsBasicFields
				control={control}
				namePlaceholder={t(
					Resources.Portfolio.AddBuildingSavings.Placeholders.nameCreate,
				)}
				contractNumberPlaceholder={t(
					Resources.Portfolio.AddShared.Placeholders.fill,
				)}
			/>
			<RequiredMinimumMessage
				control={control}
				fields={CONTRACT_REQUIRED_MINIMUM_FIELDS}
				message={getContractRequiredMinimumMessage(
					t,
					Resources.Portfolio.AddBuildingSavings.Fields,
				)}
			/>

			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddBuildingSavings.Sections.values)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<DisclosureSection
					labelCollapsed={t(
						Resources.Portfolio.AddBuildingSavings.Sections.showParameters,
					)}
					labelExpanded={t(
						Resources.Portfolio.AddBuildingSavings.Sections.hideParameters,
					)}
				>
					<FlatRowFormInput
						control={control}
						name="currentValue"
						type="number"
						label={t(Resources.Portfolio.AddBuildingSavings.Fields.value)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						suffix="Kč"
					/>
					<FlatRowFormInput
						control={control}
						name="totalInvested"
						type="number"
						label={t(Resources.Portfolio.AddBuildingSavings.Fields.invested)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						suffix="Kč"
					/>
					<FlatRowFormDatePicker
						control={control}
						name="valueDate"
						label={t(Resources.Portfolio.AddBuildingSavings.Fields.valueDate)}
						views={[DateView.Year, DateView.Month, DateView.Day]}
						displayFormat={DateFormat.Display}
						maxDate={today}
					/>
					<BuildingSavingsContractFields control={control} />
				</DisclosureSection>
			</Stack>

			<BuildingSavingsAdditionalFields control={control} />
		</>
	);
};
